import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import { formatSize, useFileDownload } from './articles/attachmentFile';
import { downloadAttachment } from './articles/api';
import { useDeleteAttachment, useTicketAttachments } from './articles/queries';

interface AttachmentsPanelProps {
  ticketId: string;
  canWrite: boolean;
}

/**
 * Alle Dateien eines Tickets an einer Stelle -- F1-11, K-27.
 *
 * Im Verlauf haengt jeder Anhang unter seinem Beitrag. Das ist richtig, wenn
 * man liest, und unbrauchbar, wenn man sucht: Bei vierzig Beitraegen ist die
 * Rechnung, die der Kunde vor drei Wochen mitgeschickt hat, nicht mehr zu
 * finden. Dieser Reiter zieht die Dateien heraus -- aus beiden Richtungen,
 * also sowohl was hereinkam als auch was zurueckging.
 *
 * **Die Herkunft steht je Zeile**, weil sie beim Suchen die eigentliche
 * Unterscheidung ist: "Was hat der Kunde geschickt?" ist eine andere Frage
 * als "Was haben wir geliefert?". Der Server leitet sie aus dem Beitrag ab.
 *
 * **Nur Herunterladen, keine Vorschau.** Der Server liefert jeden Anhang als
 * Download aus und niemals zur Anzeige (docs/04, Abschnitt 7) -- eine
 * hochgeladene HTML-Datei liefe sonst im Ursprung der Anwendung.
 */
export function AttachmentsPanel({ ticketId, canWrite }: AttachmentsPanelProps) {
  const { t, i18n } = useTranslation();
  const query = useTicketAttachments(ticketId);
  const remove = useDeleteAttachment(ticketId);
  const fetchFile = useFileDownload();

  if (query.isPending) return <SkeletonTable columns={5} />;
  if (query.isError) return <ErrorNotice error={query.error} />;

  if (query.data.length === 0) {
    return (
      <EmptyState icon="inbox" title={t('attachments.empty')} hint={t('attachments.emptyHint')} />
    );
  }

  return (
    <section className="card">
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {fetchFile.isError ? <ErrorNotice error={fetchFile.error} /> : null}

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('attachments.filename')}</th>
              <th scope="col">{t('attachments.source')}</th>
              <th scope="col">{t('attachments.size')}</th>
              <th scope="col">{t('attachments.addedAt')}</th>
              <th scope="col">
                <span className="visually-hidden">{t('tickets.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {query.data.map((attachment) => (
              <tr key={attachment.id}>
                <td>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      fetchFile.mutate({
                        fetchFile: () => downloadAttachment(attachment.id),
                        fallbackName: attachment.filename,
                      });
                    }}
                  >
                    {attachment.filename}
                  </Button>
                  {/* Ungeprueft heisst ungeprueft -- docs/04, Abschnitt 7.
                      Solange keine Virenpruefung angebunden ist, steht das
                      an der Datei. Ein Anhang ohne Hinweis saehe aus wie ein
                      geprueftes. */}
                  {attachment.virus_scanned_at === null ? (
                    <>
                      {' '}
                      <Badge tone="warning">{t('articles.notScanned')}</Badge>
                    </>
                  ) : null}
                </td>
                <td>
                  <Badge tone={attachment.source === 'customer' ? 'info' : 'success'}>
                    {t(`attachments.sourceLabel.${attachment.source}`)}
                  </Badge>
                </td>
                <td className="table__muted">{formatSize(attachment.size_bytes)}</td>
                <td className="table__muted">
                  {formatDateTime(attachment.created_at, i18n.language)}
                </td>
                <td>
                  {canWrite ? (
                    <Button
                      variant="ghost"
                      busy={remove.isPending}
                      onClick={() => {
                        if (!window.confirm(t('articles.removeAttachmentConfirm'))) return;
                        remove.mutate(attachment.id);
                      }}
                    >
                      {t('articles.removeAttachment')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
